import React, { useState, useRef, useEffect } from 'react';
import { IoPlaySharp, IoPauseSharp, IoVolumeHigh, IoVolumeMute, IoExpandOutline } from 'react-icons/io5';
import './CustomVideoPlayer.css';

const CustomVideoPlayer = ({ videoSrc, autoPlay = true }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [duration, setDuration] = useState('00:00');
  const [isMuted, setIsMuted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Formatting time Helper
  const formatTime = (time) => {
    if (isNaN(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Play/Pause
  const togglePlay = () => {
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Update Progress
  const handleTimeUpdate = () => {
    if (videoRef.current && !isDragging) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setCurrentTime(formatTime(current));
      
      const progressPercent = (current / total) * 100;
      setProgress(progressPercent);
    }
  };

  const handleLoadedMetadata = () => {
    setDuration(formatTime(videoRef.current.duration));
  };

  // Seek Logic
  const handleSeek = (e) => {
    if (progressRef.current && videoRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      let pos = (e.clientX - rect.left) / rect.width;
      pos = Math.max(0, Math.min(pos, 1)); // restrict between 0 and 1
      videoRef.current.currentTime = pos * videoRef.current.duration;
      
      // Update state instantly for silky smooth dragging
      setProgress(pos * 100);
      setCurrentTime(formatTime(pos * videoRef.current.duration));
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleSeek(e);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        handleSeek(e);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Mute
  const toggleMute = () => {
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };

  // Fullscreen
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.warn(`Error attempting to enable fullscreen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleVideoClick = () => {
    togglePlay();
  };

  return (
    <div className="custom-video-container" ref={containerRef}>
      <video
        ref={videoRef}
        src={videoSrc}
        className="custom-video"
        onClick={handleVideoClick}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        autoPlay={autoPlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      ></video>

      {/* Large Center Play Button (visible when paused) */}
      {!isPlaying && (
        <button className="center-play-btn" onClick={togglePlay}>
          <IoPlaySharp className="center-play-icon" />
        </button>
      )}

      {/* Control Bar */}
      <div className="video-controls-bar">
        <button className="control-btn play-pause-btn" onClick={togglePlay}>
          {isPlaying ? <IoPauseSharp /> : <IoPlaySharp />}
        </button>
        
        <span className="time-text current-time">{currentTime}</span>

        <div className="progress-container" onMouseDown={handleMouseDown} ref={progressRef}>
          <div className="progress-bar-bg">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        <span className="time-text total-time">{duration}</span>

        <button className="control-btn fullscreen-btn" onClick={toggleFullScreen}>
          <IoExpandOutline />
        </button>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
